import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Scale, Plus, Trash2, ToggleLeft, ToggleRight, RefreshCw } from 'lucide-react';
import { adminAPI } from '../services/api';
import type { ClassifierOverride } from '../types';

const CATEGORY_OPTIONS: Array<{ value: string; label: string; emoji: string }> = [
  { value: 'youtube',                  label: 'YouTube動画・チャンネル', emoji: '▶️' },
  { value: 'culture',                  label: '文化・エンタメ',           emoji: '🎭' },
  { value: 'economics',                label: '経済・ショッピング',        emoji: '💰' },
  { value: 'education',                label: '教育',                    emoji: '📚' },
  { value: 'tourism',                  label: '観光・旅行',              emoji: '✈️' },
  { value: 'healthcare',               label: '医療・健康',              emoji: '🏥' },
  { value: 'sports',                   label: 'スポーツ',                emoji: '⚽' },
  { value: 'politics',                 label: '政治',                    emoji: '🗳️' },
  { value: 'gambling_pachinko',        label: 'ギャンブル - パチンコ',     emoji: '🎰' },
  { value: 'gambling_online',          label: 'ギャンブル - オンライン',   emoji: '🎲' },
  { value: 'gambling_public',          label: 'ギャンブル - 公営',        emoji: '🏇' },
  { value: 'gambling_game',            label: 'ギャンブル - ゲーム',       emoji: '🎮' },
  { value: 'gambling_gacha_premium',   label: 'ギャンブル - プレミアムガチャ', emoji: '🎴' },
  { value: 'other',                    label: 'その他',                  emoji: '📱' },
];

function categoryLabel(id: string): { label: string; emoji: string } {
  const found = CATEGORY_OPTIONS.find(o => o.value === id);
  return found ? { label: found.label, emoji: found.emoji } : { label: id, emoji: '❓' };
}

export default function AIJudgeOverrides() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<ClassifierOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newDomain, setNewDomain] = useState('');
  const [newCategory, setNewCategory] = useState('youtube');
  const [newNote, setNewNote] = useState('');

  async function load() {
    setLoading(true);
    try {
      const data = await adminAPI.listClassifierOverrides();
      setRows(data);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load overrides');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleAdd() {
    if (!newDomain.trim()) return;
    setSaving(true);
    try {
      await adminAPI.upsertClassifierOverride({
        advertiserDomain: newDomain.trim(),
        category: newCategory,
        note: newNote.trim() || null,
      });
      setNewDomain(''); setNewNote('');
      await load();
    } catch (e: any) {
      alert(e?.message || t('aiJudge.addFailed'));
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(row: ClassifierOverride) {
    try {
      await adminAPI.patchClassifierOverride(row.id, { enabled: !row.enabled });
      await load();
    } catch (e: any) {
      alert(e?.message || t('aiJudge.toggleFailed'));
    }
  }

  async function handleDelete(row: ClassifierOverride) {
    if (!confirm(t('aiJudge.confirmDelete', { domain: row.advertiserDomain, category: row.overrideCategory }))) return;
    try {
      await adminAPI.deleteClassifierOverride(row.id);
      await load();
    } catch (e: any) {
      alert(e?.message || t('aiJudge.deleteFailed'));
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#1a1612] py-6">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Scale className="w-7 h-7 text-fuchsia-600 dark:text-fuchsia-400" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">{t('aiJudge.title')}</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('aiJudge.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={load}
            className="flex items-center gap-2 px-3 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg text-sm font-semibold"
          >
            <RefreshCw className="w-4 h-4" />
            {t('aiJudge.refresh')}
          </button>
        </div>

        {/* Add new override */}
        <div className="bg-white dark:bg-warm-gray-800 rounded-xl border border-gray-200 dark:border-warm-gray-700 p-4 mb-4 shadow-subtle">
          <h2 className="text-sm font-bold mb-3 text-gray-700 dark:text-gray-200">{t('aiJudge.addNewRule')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
            <div className="md:col-span-4">
              <label className="block text-xs text-gray-500 mb-1">{t('aiJudge.advertiserDomain')}</label>
              <input
                type="text"
                value={newDomain}
                onChange={e => setNewDomain(e.target.value)}
                placeholder={t('aiJudge.domainPlaceholder') as string}
                className="w-full px-3 py-2 border border-gray-300 dark:border-warm-gray-600 rounded text-sm dark:bg-warm-gray-700 dark:text-gray-100"
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs text-gray-500 mb-1">{t('aiJudge.category')}</label>
              <select
                value={newCategory}
                onChange={e => setNewCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-warm-gray-600 rounded text-sm dark:bg-warm-gray-700 dark:text-gray-100"
              >
                {CATEGORY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.emoji} {opt.label}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs text-gray-500 mb-1">{t('aiJudge.noteLabel')}</label>
              <input
                type="text"
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                placeholder={t('aiJudge.notePlaceholder') as string}
                className="w-full px-3 py-2 border border-gray-300 dark:border-warm-gray-600 rounded text-sm dark:bg-warm-gray-700 dark:text-gray-100"
              />
            </div>
            <div className="md:col-span-2 flex items-end">
              <button
                onClick={handleAdd}
                disabled={saving || !newDomain.trim()}
                className="w-full flex items-center justify-center gap-1 px-4 py-2 bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded font-semibold transition-colors text-sm disabled:bg-gray-400"
              >
                <Plus className="w-4 h-4" />
                {t('aiJudge.add')}
              </button>
            </div>
          </div>
        </div>

        {/* Existing overrides */}
        <div className="bg-white dark:bg-warm-gray-800 rounded-xl border border-gray-200 dark:border-warm-gray-700 shadow-subtle">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-warm-gray-700 flex items-center justify-between">
            <h2 className="text-sm font-bold text-gray-700 dark:text-gray-200">{t('aiJudge.registeredRules')}</h2>
            <span className="text-xs text-gray-500">{rows.length}</span>
          </div>
          {error && <div className="p-4 text-sm text-red-600 dark:text-red-400">{error}</div>}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-warm-gray-700">
                <tr className="text-left text-xs text-gray-500 dark:text-gray-300">
                  <th className="px-3 py-2">{t('aiJudge.status')}</th>
                  <th className="px-3 py-2">{t('aiJudge.advertiserDomain')}</th>
                  <th className="px-3 py-2">{t('aiJudge.category')}</th>
                  <th className="px-3 py-2 text-right">{t('aiJudge.matchedEvents')}</th>
                  <th className="px-3 py-2">{t('aiJudge.note')}</th>
                  <th className="px-3 py-2">{t('aiJudge.createdBy')}</th>
                  <th className="px-3 py-2">{t('aiJudge.createdAt')}</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-500">{t('common.loading')}</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-500">{t('aiJudge.noRulesYet')}</td></tr>
                )}
                {rows.map(row => {
                  const cat = categoryLabel(row.overrideCategory);
                  return (
                    <tr key={row.id} className="border-t border-gray-100 dark:border-warm-gray-700 hover:bg-gray-50 dark:hover:bg-warm-gray-700/40">
                      <td className="px-3 py-2">
                        <button onClick={() => handleToggle(row)} className="inline-flex items-center" title={row.enabled ? t('aiJudge.disableTooltip') as string : t('aiJudge.enableTooltip') as string}>
                          {row.enabled
                            ? <ToggleRight className="w-6 h-6 text-emerald-500" />
                            : <ToggleLeft  className="w-6 h-6 text-gray-400" />}
                        </button>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-800 dark:text-gray-200">{row.advertiserDomain}</td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/30 dark:text-fuchsia-200 text-xs font-semibold">
                          {cat.emoji} {cat.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{row.matchedEventCount.toLocaleString()}</td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{row.note || '-'}</td>
                      <td className="px-3 py-2 text-gray-500 text-xs">{row.createdBy}</td>
                      <td className="px-3 py-2 text-gray-500 text-xs">{new Date(row.createdAt).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', hour12: false })}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => handleDelete(row)} className="text-red-600 hover:text-red-800 inline-flex items-center gap-1 text-xs">
                          <Trash2 className="w-4 h-4" /> {t('aiJudge.delete')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
